import {
  LayoutDashboard,
  Inbox,
  FolderOpen,
  Users,
  CheckSquare,
  ClipboardCheck,
  Files,
  ReceiptText,
  Settings,
  Bell,
  ClipboardList,
  Palette,
  Timer,
  CalendarDays,
  BarChart3,
} from 'lucide-react';
import { FaBookOpen, FaGlobe, type AppIcon } from '../ui/FaIcons';
import type { Feature } from '../../services/auth/permissions';

export interface NavItemDef {
  icon: AppIcon;
  label: string;
  /** Route cible. Ignoré quand `action` est défini (l'item devient un bouton). */
  to: string;
  /**
   * Action déclenchée au clic, à la place d'une navigation.
   * 'open-messaging-sidecar' — ouvre la sidecar de messagerie.
   */
  action?: 'open-messaging-sidecar';
  /**
   * Feature key from RESTRICTED_FEATURES.
   * When set, the nav item is only shown to roles that can access that feature.
   * When absent, the item is always visible to authenticated users.
   */
  feature?: Feature;
  disabled?: boolean;
  separatorAfter?: boolean;
  /** Indent the item visually to show it is a sub-item */
  indent?: boolean;
  /**
   * When set, the SidebarNav will show a numeric badge sourced from the specified data.
   * 'tickets_unseen' — uses useTicketCounts().unseen_mine
   * 'messaging_unread' — uses useTotalUnreadCount() from useMessaging
   */
  badgeCountSource?: 'tickets_unseen' | 'messaging_unread' | 'gmail_queued' | 'parapheur_pending';
}

export const NAV_ITEMS: NavItemDef[] = [
  {
    icon: LayoutDashboard,
    label: 'Tableau de bord',
    to: '/dashboard',
    feature: 'dashboard',
  },
  {
    icon: Inbox,
    label: 'Demandes',
    to: '/tickets',
    feature: 'tickets_view',
    badgeCountSource: 'tickets_unseen',
    separatorAfter: true,
  },
  {
    icon: FolderOpen,
    label: 'Dossiers',
    to: '/dossiers',
    feature: 'dossiers_view',
  },
  {
    icon: Timer,
    label: 'Feuille de temps',
    to: '/feuille-de-temps',
    feature: 'timesheet_view',
  },
  {
    icon: CalendarDays,
    label: 'Calendrier',
    to: '/calendrier',
    feature: 'calendar_view',
  },
  // « Boite Gmail » retirée de la sidebar (demande Charles 21/07) — la route
  // /gmail-inbox et la feature gmail_inbox_view restent en place.
  {
    icon: ClipboardCheck,
    label: 'Parapheur',
    to: '/parapheur',
    feature: 'signature_my_parapheur',
    badgeCountSource: 'parapheur_pending',
  },
  {
    icon: FaGlobe,
    label: 'e-Barreau',
    to: '/e-barreau',
    feature: 'ebarreau_view',
  },
  {
    icon: Users,
    label: 'Répertoire',
    to: '/contacts',
    feature: 'contacts_view',
  },
  {
    icon: CheckSquare,
    label: 'Tâches',
    to: '/taches',
    feature: 'tasks_view',
  },
  {
    icon: Files,
    label: 'Fichiers',
    to: '/fichiers',
    feature: 'files_view',
  },
  {
    icon: FaBookOpen,
    label: 'Bibliothèque',
    to: '/bibliotheque',
    feature: 'cabinet_library',
    separatorAfter: true,
  },
  {
    icon: ReceiptText,
    label: 'Facturation',
    to: '/facturation',
    feature: 'billing_view',
    separatorAfter: true,
  },
  {
    icon: BarChart3,
    label: 'Statistiques',
    to: '/stats',
    feature: 'stats_cabinet_view',
  },
  {
    icon: Bell,
    label: 'Notifications',
    to: '/notifications',
    feature: 'notifications_view',
  },
  {
    icon: ClipboardList,
    label: 'Logs',
    to: '/logs',
    feature: 'logs_view',
  },
  {
    icon: Palette,
    label: 'Design System',
    to: '/design-system',
    feature: 'design_system',
  },
  {
    icon: Settings,
    label: 'Param\u00e8tres',
    to: '/parametres',
    feature: 'settings_team',
  },
];
